import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchLocations,
  fetchLocation,
  createLocation,
  updateLocation,
} from '../services/inventoryService';

/**
 * @typedef {Object} Location
 * @property {string} id - Location ID
 * @property {string} name - Location name
 * @property {string} code - Location code
 * @property {string} type - Location type
 * @property {string} address - Location address
 * @property {string} [description] - Location description
 */

/**
 * Hook to fetch all locations
 * @returns {import('@tanstack/react-query').UseQueryResult<Location[], Error>}
 */
export function useLocations() {
  return useQuery({
    queryKey: ['inventory', 'locations'],
    queryFn: fetchLocations,
  });
}

/**
 * Hook to fetch a single location by ID
 * @param {string} id - ID of the location to fetch
 * @returns {import('@tanstack/react-query').UseQueryResult<Location, Error>}
 */
export function useLocation(id) {
  return useQuery({
    queryKey: ['inventory', 'locations', id],
    queryFn: () => fetchLocation(id),
  });
}

/**
 * Hook to create a new location
 * @returns {import('@tanstack/react-query').UseMutationResult<Location, Error, Omit<Location, 'id'>>}
 */
export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'locations'] });
    },
  });
}

/**
 * Hook to update a location
 * @param {string} id - ID of the location to update
 * @returns {import('@tanstack/react-query').UseMutationResult<Location, Error, Partial<Location>>}
 */
export function useUpdateLocation(id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => updateLocation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'locations'] });
    },
  });
} 